import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Body2, Button, ConfirmDialog, LayerContextProvider, layers } from "@activecollab/components";
import classNames from "classnames";
import { Formik, Form, Field } from "formik";
import { List } from "immutable";
import PropTypes from "prop-types";
import AngDropFiles from "~/components/component.ang_drop_files";
import { CustomFormField } from "~/components/CustomFormField";
import { FormikEffect } from "~/components/FormikEffect";
import { useFileHandler } from "~/components/Hooks";
import { FormCheckbox } from "~/components/ModalObjects";
import { AttachmentUpload } from "~/components/select_attachments/AttachmentUpload";
import { SelectAttachments } from "~/components/select_attachments/SelectAttachments";
import { SubscribersAvatarList } from "~/components/SubscribersAvatarList/SubscribersAvatarList";
import { SubscribersPicker } from "~/components/tasks/TaskPanel/subscribers/SubscribersPicker";
import VisualEditorContainer from "~/components/VisualEditor/VisualEditorContainer";
import { keyCodes } from "~/utils/key_codes";
import { DiscussionAndNoteFormValidator } from "~/validators/validate.discussionsAndNotes";

const normalizeData = (attributes) => {
  const { attachments, ...rest } = attributes;

  return {
    attach_uploaded_files: attachments.filesToAttach,
    drop_attached_files: attachments.filesToDrop,
    ...rest,
  };
};

const DiscussionForm = ({
  id,
  name,
  body,
  subscribers,
  isHiddenFromClients,
  isPinned,
  attachments,
  projectId,
  bodyMode,
  isLoggedUserClient,
  onCancel,
  onSubmit,
  onChange,
  hasDraft,
  attachUploadedFiles,
  dropAttachedFiles,
  submitButtonLabel,
  hideFromClients,
  className,
  projectMembers,
  shouldOverflow,
}) => {
  const { t } = useTranslation();
  const inputRef = useRef(null);
  const submitButtonRef = useRef(null);
  const fileInputRef = useRef(null);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const { uploading, onUpload, ...fileHandlerRest } = useFileHandler({
    context: "discussion",
  });

  const projectMembersIds = useMemo(() => projectMembers.map((u) => u.id), [projectMembers]);

  const handleOpenFileWindow = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleChange = useCallback(
    (prevAttributes, nextAttributes) => {
      if (nextAttributes.is_hidden_from_clients) {
        const clientIds = projectMembers.reduce((prev, next) => {
          if (next.class === "Client") {
            prev.push(next.id);
          }
          return prev;
        }, []);
        nextAttributes.subscribers = nextAttributes.subscribers.reduce((prev, next) => {
          if (!clientIds.includes(next)) {
            prev.push(next);
          }
          return prev;
        }, []);
      }
      onChange(normalizeData(nextAttributes));
    },
    [onChange, projectMembers]
  );

  useEffect(() => {
    inputRef.current.focus();
  }, []);

  const handleConfirmModalSubmit = useCallback(() => {
    if (typeof onCancel === "function") {
      onCancel();
    }
    setShowConfirmationModal(false);
  }, [onCancel]);

  const handleCloseConfirmationModal = useCallback(() => {
    setShowConfirmationModal(false);
  }, []);

  const handleCancel = useCallback(() => {
    if (hasDraft) {
      setShowConfirmationModal(true);
    } else if (typeof onCancel === "function") {
      onCancel();
    }
  }, [hasDraft, onCancel]);

  const handleUpload = useCallback(
    (files) => {
      for (let i = 0; i < files.length; i++) {
        onUpload(files[i]);
      }
    },
    [onUpload]
  );

  const handleSubmit = useCallback(
    (attributes) => {
      attributes.reset_attachments = true;
      attributes.reset_subscribers = true;
      onSubmit(normalizeData(attributes));
    },
    [onSubmit]
  );

  const handleKeyPress = useCallback(
    (event) => {
      if (submitButtonRef.current && event.key === keyCodes.enter && (event.ctrlKey || event.metaKey)) {
        event.preventDefault();
        submitButtonRef.current.click();
      }
      if (event.key === keyCodes.escape) {
        event.preventDefault();
        if (hasDraft) {
          setShowConfirmationModal(true);
        } else if (typeof onCancel === "function") {
          onCancel();
        }
      }
    },
    [onCancel, hasDraft]
  );

  const handleKeyPressEditor = useCallback((event) => {
    if (event.key === keyCodes.enter && (event.ctrlKey || event.metaKey)) {
      event.preventDefault();
    }
  }, []);

  return (
    <Formik
      initialValues={{
        name,
        body,
        subscribers,
        is_hidden_from_clients: isHiddenFromClients,
        is_pinned: isPinned,
        attachments: {
          filesToAttach: attachUploadedFiles,
          filesToDrop: dropAttachedFiles,
        },
      }}
      validationSchema={DiscussionAndNoteFormValidator}
      onSubmit={handleSubmit}
    >
      {({ isSubmitting, isValid }) => (
        <>
          <Form
            className={classNames("tw-w-full tw-max-h-full tw-flex tw-flex-col", className)}
            onKeyDown={handleKeyPress}
          >
            <FormikEffect onChange={handleChange} />
            <div
              className={classNames("tw-flex-1 tw-rounded-md tw-bg-page-paper-main tw-flex tw-flex-col tw-max-h-full", {
                "lg:tw-overflow-y-auto": shouldOverflow,
              })}
            >
              <Field name="name">
                {({ field }) => (
                  <input
                    ref={inputRef}
                    id="discussionNameInput"
                    type="text"
                    tabIndex={0}
                    maxLength={150}
                    placeholder={`${t("Discussion name")} *`}
                    data-testid="project-discussion-name-input"
                    className="tw-text-lg tw-font-bold tw-px-6 tw-py-4 tw-outline-none tw-w-full tw-bg-transparent tw-text-theme-900 tw-border-border-primary tw-border-solid tw-border tw-rounded-t-md tw-border-b-0"
                    {...field}
                  />
                )}
              </Field>

              <div
                className={classNames(
                  "tw-border-border-primary tw-border-solid tw-border tw-flex-1 tw-border-t-transparent",
                  { "lg:tw-overflow-y-auto": shouldOverflow }
                )}
              >
                <CustomFormField name="body">
                  {({ field }) => (
                    <VisualEditorContainer
                      projectId={projectId}
                      parentType="Discussion"
                      parentId={id}
                      content={field.value}
                      bodyMode={bodyMode}
                      onKeyDown={handleKeyPressEditor}
                      onChange={field.onChange}
                      placeholder={t(`Write a description or press "/" for commands`)}
                    />
                  )}
                </CustomFormField>
              </div>

              <div className="tw-px-6 tw-py-5 tw-border tw-border-border-primary tw-border-solid tw-overflow-hidden tw-border-t-transparent tw-shrink-0">
                <CustomFormField name="attachments">
                  {({ field }) => (
                    <SelectAttachments
                      {...fileHandlerRest}
                      uploading={uploading}
                      attachmentIds={attachments}
                      filesToAttach={field.value.filesToAttach}
                      filesToDrop={field.value.filesToDrop}
                      onChange={field.onChange}
                      onOpenFileWindow={handleOpenFileWindow}
                    />
                  )}
                </CustomFormField>
              </div>
              <div
                className={classNames(
                  "tw-px-6 tw-h-[66px] tw-flex tw-items-center tw-border tw-border-border-primary tw-border-solid tw-overflow-hidden tw-border-t-transparent tw-shrink-0",
                  { "tw-rounded-b-md": isLoggedUserClient }
                )}
              >
                <Body2
                  weight="bold"
                  className="tw-truncate tw-w-[100px] tw-mr-1"
                >
                  {t("Subscribers")}
                </Body2>
                <CustomFormField name="subscribers">
                  {({ field }) => {
                    return (
                      <SubscribersPicker
                        onChange={field.onChange}
                        hideClients={isHiddenFromClients}
                        selected={field.value}
                        userIds={projectMembersIds}
                        target={
                          <SubscribersAvatarList
                            subscriberIds={field.value}
                            data-testid="subscribers-picker"
                          />
                        }
                      />
                    );
                  }}
                </CustomFormField>
              </div>
              {!isLoggedUserClient ? (
                <div className="tw-px-7 tw-py-5 tw-border tw-border-border-primary tw-border-solid tw-overflow-hidden tw-border-t-transparent tw-rounded-b-md tw-shrink-0">
                  {hideFromClients ? (
                    <CustomFormField name="is_hidden_from_clients">
                      {({ field }) => (
                        <FormCheckbox
                          id="discussion_form_hidden_from_clients"
                          checked={field.value}
                          onChange={field.onChange}
                          className="form_field tw-mb-2"
                          text={t("Hidden from clients")}
                        />
                      )}
                    </CustomFormField>
                  ) : null}
                  <CustomFormField name="is_pinned">
                    {({ field }) => (
                      <FormCheckbox
                        id="discussion_form_is_pinned"
                        checked={field.value}
                        onChange={field.onChange}
                        className="form_field"
                        text={t("Pinned to top")}
                      />
                    )}
                  </CustomFormField>
                </div>
              ) : null}
              <AngDropFiles onDrop={handleUpload} />
            </div>
            <div className="tw-pt-4">
              <Button
                ref={submitButtonRef}
                type="submit"
                variant="primary"
                disabled={isSubmitting || !isValid || uploading}
                tabIndex={0}
                className="tw-mr-3"
                data-testid="submit-discussion"
              >
                {uploading ? `${t("Uploading")}...` : submitButtonLabel}
              </Button>
              <Button
                type="button"
                variant="secondary"
                onClick={handleCancel}
                tabIndex={0}
                data-testid="cancel-discussion"
              >
                {t("Cancel")}
              </Button>
            </div>
          </Form>
          <LayerContextProvider value={{ zIndex: layers.skyscraper }}>
            <ConfirmDialog
              open={showConfirmationModal}
              dialogTitle={t("Discard changes?")}
              dialogContent={t("All unsaved changes will be lost.")}
              confirmBtnText={t("Discard")}
              cancelBtnText={t("Cancel")}
              onConfirm={handleConfirmModalSubmit}
              onCancel={handleCloseConfirmationModal}
            />
          </LayerContextProvider>
          <AttachmentUpload
            ref={fileInputRef}
            multiple
            onUpload={onUpload}
          />
        </>
      )}
    </Formik>
  );
};

DiscussionForm.propTypes = {
  id: PropTypes.number,
  name: PropTypes.string,
  projectId: PropTypes.number.isRequired,
  body: PropTypes.string,
  attachments: PropTypes.instanceOf(List),
  subscribers: PropTypes.array,
  onCancel: PropTypes.func,
  onSubmit: PropTypes.func,
  onChange: PropTypes.func,
  isHiddenFromClients: PropTypes.bool,
  isPinned: PropTypes.bool,
  bodyMode: PropTypes.string,
  isLoggedUserClient: PropTypes.bool,
  hasDraft: PropTypes.bool,
  submitButtonLabel: PropTypes.string,
  attachUploadedFiles: PropTypes.arrayOf(PropTypes.string),
  dropAttachedFiles: PropTypes.arrayOf(PropTypes.number),
  hideFromClients: PropTypes.bool,
  className: PropTypes.string,
  projectMembers: PropTypes.array,
  shouldOverflow: PropTypes.bool,
};

DiscussionForm.defaultProps = {
  id: 0,
  name: "",
  body: "",
  attachments: List(),
  subscribers: [],
  onCancel: () => null,
  onSubmit: () => null,
  onChange: () => null,
  isHiddenFromClients: false,
  isPinned: false,
  bodyMode: "paragraph",
  isLoggedUserClient: false,
  hasDraft: false,
  attachUploadedFiles: [],
  dropAttachedFiles: [],
  projectMembers: [],
};

export default DiscussionForm;
